import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StablebondProgram } from '@etherfuse/stablebond-sdk';
import Decimal from 'decimal.js';
import { PublicKey, VersionedTransaction } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { TOKEN_PROGRAM_ID, getAssociatedTokenAddress } from '@solana/spl-token';
import { useWallet } from '../hooks/useWallet';
import { useAuth } from '../hooks/useAuth';
import { useWalletContext } from '../context/WalletContext';
import { useUser } from '@civic/auth-web3/react';
import styles from './PerenaPool.module.css';
import { CONFIG } from '../config';
import { logger } from '../utils/logger';

// Constants
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');

// Helper component for clickable links
const ClickableLink = ({ url, text }) => {
  if (!url) return 'N/A';
  const displayText = text || url.replace(/^https?:\/\//, '').split('/')[0];
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer"
      style={{ color: '#3b82f6', textDecoration: 'none', wordBreak: 'break-all' }}
      title={url}
    >
      {displayText}
    </a>
  );
};

const BondDetail = () => {
  const { bondAddress } = useParams();
  const navigate = useNavigate();
  const { wallet } = useWalletContext();
  const { email } = useAuth();
  const userContext = useUser();
  const civicWallet = userContext?.solana?.wallet;
  
  const [bond, setBond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState('');
  const [showErrorToast, setShowErrorToast] = React.useState(false);
  const [showSuccessToast, setShowSuccessToast] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');

  const showSuccessNotification = (message) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 5000); // Auto-hide after 5 seconds
  };

  useEffect(() => {
    const fetchBond = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all bonds and find the one matching bondAddress
        const allBonds = await StablebondProgram.getBonds(CONFIG.RPC_URL);
        const found = allBonds.find(
          b => (b.address === bondAddress) ||
               (b.mint?.toBase58?.() === bondAddress) ||
               (typeof b.mint === 'string' && b.mint === bondAddress)
        );
        logger.log('All bonds:', allBonds);
        logger.log('Looking for:', bondAddress, 'Found:', found);
        setBond(found || null);
        if (!found) setError('No bond found for this address.');
      } catch (err) {
        logger.error('Error fetching bond:', err);
        setError('Failed to load bond details.');
      } finally {
        setLoading(false);
      }
    };
    if (bondAddress) fetchBond();
    else {
      setError('No bond address provided in URL.');
      setLoading(false);
    }
  }, [bondAddress]);

  // Helper for formatting numbers
  // Enhanced formatNumber: handles Decimal.js, SDK Decimals, numbers, and strings
  const formatNumber = (num, digits = 2) => {
    if (!num) return '0.00';
    // Handle Decimal.js or SDK Decimal objects
    if (typeof num === 'object' && num !== null) {
      // If it's already a Decimal.js
      if (Decimal.isDecimal(num)) return num.toFixed(digits);
      // If it's the SDK's Decimal2 type (has d, e, s)
      if ('d' in num && 'e' in num && 's' in num) {
        try {
          // Convert to Decimal.js
          return new Decimal(num.toString()).toFixed(digits);
        } catch {
          // Try manual conversion
          if (Array.isArray(num.d)) {
            // d is the digits array, e is exponent
            const str = num.d.join('') + 'e' + num.e;
            return new Decimal(str).toFixed(digits);
          }
        }
      }
    }
    // If it's a string or number
    return new Decimal(num).toFixed(digits);
  };

  const handleDeposit = async () => {
    logger.log('=== Starting bond purchase process ===');
    
    if (!civicWallet?.publicKey) {
      const errorMsg = 'Error: Please connect your wallet first';
      logger.error(errorMsg);
      setActionError(errorMsg);
      setShowErrorToast(true);
      return;
    }

    try {
      setActionLoading(true);
      setActionError(null);
      
      const amount = parseFloat(depositAmount);
      if (isNaN(amount) || amount <= 0) {
        const errorMsg = 'Error: Please enter a valid amount';
        logger.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Log detailed bond information for debugging
      logger.log('Bond details:', {
        bondObject: bond,
        bondAddressFromProps: bondAddress,
        bondMintAddress: bond?.mint,
        bondMintAddressString: bond?.mint?.toBase58 ? bond.mint.toBase58() : bond?.mint,
        bondMintType: bond?.mint ? typeof bond.mint : 'undefined'
      });

      // Get the correct mint address to use for the swap
      // Always use the SPL token mint address for outputMint
      let bondMintAddressString = '';
      if (bond?.mint?.address && typeof bond.mint.address === 'string') {
        bondMintAddressString = bond.mint.address;
      } else {
        // fallback, try legacy or fallback to bondAddress
        if (typeof bond?.mint === 'string') {
          bondMintAddressString = bond.mint;
        } else if (bond?.mint?.toBase58) {
          bondMintAddressString = bond.mint.toBase58();
        } else if (bondAddress) {
          bondMintAddressString = bondAddress;
        }
      }
      if (!bondMintAddressString) {
        throw new Error('Bond mint address not found in bond details');
      }

      logger.log('Using bond mint address for swap:', bondMintAddressString);
      logger.log('Bond purchase parameters:', {
        bondAddress: bondAddress,
        bondMintAddress: bondMintAddressString,
        amount: amount.toString(),
        wallet: civicWallet.publicKey.toString(),
        network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
      });

      // Convert amount to lamports (USDC has 6 decimals)
      const amountInLamports = Math.round(amount * 1e6).toString();
      
      // Create Jupiter swap order
      const orderParams = new URLSearchParams({
        inputMint: USDC_MINT.toBase58(),
        outputMint: bondMintAddressString, // Using bond's mint address as output
        amount: amountInLamports,
        slippageBps: '100', // 1% slippage
        taker: civicWallet.publicKey.toBase58()
      });

      logger.log('Creating Jupiter swap order with params:', {
        ...Object.fromEntries(orderParams),
        // Log the full URL for debugging
        fullUrl: `https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`
      });
      
      // Get the swap order from Jupiter
      const orderResponse = await fetch(`https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        throw new Error(errorText || 'Failed to create swap order');
      }

      const orderData = await orderResponse.json();
      if (!orderData.transaction || !orderData.requestId) {
        throw new Error('Invalid swap order response');
      }

      // Deserialize the transaction
      const transactionBytes = Uint8Array.from(atob(orderData.transaction), c => c.charCodeAt(0));
      const transaction = VersionedTransaction.deserialize(transactionBytes);
      
      // Sign the transaction with the user's wallet
      logger.log('Signing transaction...');
      const signedTransaction = await civicWallet.signTransaction(transaction);
      
      // Serialize the signed transaction
      const serializedTransaction = btoa(String.fromCharCode.apply(null, signedTransaction.serialize()));

      // Execute the swap
      logger.log('Executing swap...');
      const executeResponse = await fetch('https://lite-api.jup.ag/ultra/v1/execute', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          signedTransaction: serializedTransaction,
          requestId: orderData.requestId
        })
      });

      if (!executeResponse.ok) {
        const errorText = await executeResponse.text();
        throw new Error(errorText || 'Failed to execute swap');
      }

      const executeData = await executeResponse.json();
      
      if (executeData.status === 'Success') {
        // Wait for transaction confirmation
        logger.log('Waiting for transaction confirmation...');
        await rpc.confirmTransaction(executeData.signature, 'confirmed');
        
        logger.log('Swap executed successfully:', {
          signature: executeData.signature,
          explorerUrl: `https://explorer.solana.com/tx/${executeData.signature}?cluster=${CONFIG.SOLANA_NETWORK || 'mainnet-beta'}`
        });
        
        // Close modal and reset form
        setShowDepositModal(false);
        setDepositAmount('');
        
        // Show success notification
        showSuccessNotification(`Successfully purchased ${amount} of ${bond?.name || 'bond'}`);

        // Record the purchase in the backend database (non-blocking for UX)
        try {
          // Determine price and shares
          const priceUsdc = (bond?.currentIssuance?.startingTokenAmount != null)
            ? Number(bond.currentIssuance.startingTokenAmount)
            : null;
          const shares = priceUsdc && priceUsdc > 0
            ? Number(amount) / priceUsdc
            : Number(amount); // fallback if price unavailable

          const payload = {
            userId: email || civicWallet.publicKey.toBase58(),
            walletAddress: civicWallet.publicKey.toBase58(),
            bondAddress: bondAddress,
            bondMintAddress: bondMintAddressString,
            bondSymbol: bond?.mint?.symbol || null,
            bondName: bond?.mint?.name || null,
            amountUsdc: Number(amount),
            shares: Number(shares),
            priceUsdc: priceUsdc,
            txSignature: executeData.signature,
            network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
          };

          logger.log('Recording bond purchase:', payload);
          const resp = await fetch('/api/bonds/deposit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!resp.ok) {
            const text = await resp.text();
            logger.error('Failed to record bond purchase:', text);
          } else {
            const data = await resp.json();
            logger.log('Recorded bond purchase:', data);
          }
        } catch (recordErr) {
          logger.error('Error recording bond purchase (non-blocking):', recordErr);
        }

        // Refresh bond data
        // You might want to implement a refresh function here

        return { success: true, signature: executeData.signature };
      } else {
        throw new Error(executeData.error || 'Swap execution failed');
      }
      
    } catch (err) {
      const errorMsg = `❌ Bond purchase failed: ${err.message || 'Unknown error'}`;
      logger.error(errorMsg);
      logger.error('Error details:', err);
      
      setActionError(err.message || 'Failed to process bond purchase');
      setShowErrorToast(true);
      
      return { success: false, error: err.message };
      
    } finally {
      logger.log('=== Bond purchase process completed ===');
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    logger.log('=== Starting bond withdrawal process ===');
    
    if (!civicWallet?.publicKey) {
      const errorMsg = 'Error: Please connect your wallet first';
      logger.error(errorMsg);
      setActionError(errorMsg);
      setShowErrorToast(true);
      return;
    }

    try {
      setActionLoading(true);
      setActionError(null);
      
      const amount = parseFloat(withdrawAmount);
      if (isNaN(amount) || amount <= 0) {
        const errorMsg = 'Error: Please enter a valid amount';
        logger.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Get the bond's mint address
      let bondMintAddressString = '';
      if (bond?.mint?.address && typeof bond.mint.address === 'string') {
        bondMintAddressString = bond.mint.address;
      } else if (typeof bond?.mint === 'string') {
        bondMintAddressString = bond.mint;
      } else if (bond?.mint?.toBase58) {
        bondMintAddressString = bond.mint.toBase58();
      } else if (bondAddress) {
        bondMintAddressString = bondAddress;
      }

      if (!bondMintAddressString) {
        throw new Error('Bond mint address not found in bond details');
      }

      logger.log('Using bond mint address for withdrawal:', bondMintAddressString);
      logger.log('Bond withdrawal parameters:', {
        bondAddress: bondAddress,
        bondMintAddress: bondMintAddressString,
        amount: amount.toString(),
        wallet: civicWallet.publicKey.toString(),
        network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
      });

      // Create Jupiter swap order (reverse of deposit)
      const orderParams = new URLSearchParams({
        inputMint: bondMintAddressString, // Bond token as input
        outputMint: USDC_MINT.toBase58(), // USDC as output
        amount: Math.round(amount * 1e6).toString(), // Assuming 6 decimals for the bond token
        slippageBps: '100', // 1% slippage
        taker: civicWallet.publicKey.toBase58()
      });

      logger.log('Creating Jupiter swap order with params:', {
        ...Object.fromEntries(orderParams),
        fullUrl: `https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`
      });
      
      // Get the swap order from Jupiter
      const orderResponse = await fetch(`https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        throw new Error(errorText || 'Failed to create swap order');
      }

      const orderData = await orderResponse.json();
      if (!orderData.transaction || !orderData.requestId) {
        throw new Error('Invalid swap order response');
      }

      // Deserialize the transaction
      const transactionBytes = Uint8Array.from(atob(orderData.transaction), c => c.charCodeAt(0));
      const transaction = VersionedTransaction.deserialize(transactionBytes);
      
      // Sign the transaction with the user's wallet
      logger.log('Signing withdrawal transaction...');
      const signedTransaction = await civicWallet.signTransaction(transaction);
      
      // Serialize the signed transaction
      const serializedTransaction = btoa(String.fromCharCode.apply(null, signedTransaction.serialize()));

      // Execute the swap
      logger.log('Executing withdrawal swap...');
      const executeResponse = await fetch('https://lite-api.jup.ag/ultra/v1/execute', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          signedTransaction: serializedTransaction,
          requestId: orderData.requestId
        })
      });

      if (!executeResponse.ok) {
        const errorText = await executeResponse.text();
        throw new Error(errorText || 'Failed to execute withdrawal swap');
      }

      const executeData = await executeResponse.json();
      
      if (executeData.status === 'Success') {
        // Wait for transaction confirmation
        logger.log('Waiting for withdrawal transaction confirmation...');
        await rpc.confirmTransaction(executeData.signature, 'confirmed');
        
        logger.log('Withdrawal swap executed successfully:', {
          signature: executeData.signature,
          explorerUrl: `https://explorer.solana.com/tx/${executeData.signature}?cluster=${CONFIG.SOLANA_NETWORK || 'mainnet-beta'}`
        });
        
        // Close modal and reset form
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        
        // Show success message
        const successMsg = `✅ Successfully withdrew ${amount} bond tokens to USDC!`;
        logger.log(successMsg);
        
        // Record the withdrawal in the backend database (non-blocking)
        try {
          const priceUsdc = (bond?.currentIssuance?.startingTokenAmount != null)
            ? Number(bond.currentIssuance.startingTokenAmount)
            : null;
          const sharesOut = Number(amount);
          const amountUsdc = priceUsdc ? Number((sharesOut * priceUsdc).toFixed(6)) : Number(amount);

          // Resolve mint address consistently
          let bondMintAddressStringLocal = '';
          if (bond?.mint?.address && typeof bond.mint.address === 'string') {
            bondMintAddressStringLocal = bond.mint.address;
          } else if (typeof bond?.mint === 'string') {
            bondMintAddressStringLocal = bond.mint;
          } else if (bond?.mint?.toBase58) {
            bondMintAddressStringLocal = bond.mint.toBase58();
          } else if (bondAddress) {
            bondMintAddressStringLocal = bondAddress;
          }

          const payload = {
            userId: email || civicWallet.publicKey.toBase58(),
            walletAddress: civicWallet.publicKey.toBase58(),
            bondAddress: bondAddress,
            bondMintAddress: bondMintAddressStringLocal,
            bondSymbol: bond?.mint?.symbol || null,
            bondName: bond?.mint?.name || null,
            amountUsdc: amountUsdc, // positive; backend will negate
            shares: sharesOut,      // positive; backend will negate
            priceUsdc: priceUsdc,
            txSignature: executeData.signature,
            network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
          };

          logger.log('Recording bond withdrawal:', payload);
          const resp = await fetch('/api/bonds/withdraw', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!resp.ok) {
            const text = await resp.text();
            logger.error('Failed to record bond withdrawal:', text);
          } else {
            const data = await resp.json();
            logger.log('Recorded bond withdrawal:', data);
          }
        } catch (recordErr) {
          logger.error('Error recording bond withdrawal (non-blocking):', recordErr);
        }

        // Here you might want to refresh the user's balance or bond data
        
        return { success: true, signature: executeData.signature };
      } else {
        throw new Error(executeData.error || 'Withdrawal swap execution failed');
      }
      
    } catch (err) {
      const errorMsg = `❌ Bond withdrawal failed: ${err.message || 'Unknown error'}`;
      logger.error(errorMsg);
      logger.error('Error details:', err);
      
      setActionError(err.message || 'Failed to process bond withdrawal');
      setShowErrorToast(true);
      
      return { success: false, error: err.message };
      
    } finally {
      logger.log('=== Bond withdrawal process completed ===');
      setActionLoading(false);
    }
  };

  const handleCloseErrorToast = () => setShowErrorToast(false);

  React.useEffect(() => {
    if (showDepositModal || showWithdrawModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showDepositModal, showWithdrawModal]);

  if (loading) {
    return (
      <div className={styles.container} style={{ padding: '2rem 1rem' }}>
        <div className={styles.spinner} />
        <div style={{ marginTop: '1rem', color: '#64748b', fontSize: '1.1rem' }}>Loading bond details...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.container} style={{ padding: '2rem 1rem' }}>
        <div className={styles.errorMessage}>{error}</div>
        <button className="action-btn" onClick={() => navigate(-1)} style={{ marginTop: '2rem' }}>Back</button>
      </div>
    );
  }
  if (!bond) {
    return (
      <div className={styles.container} style={{ padding: '2rem 1rem' }}>
        <div className={styles.errorMessage}>No bond found for this address.</div>
        <button className="action-btn" onClick={() => navigate(-1)} style={{ marginTop: '2rem' }}>Back</button>
      </div>
    );
  }

  // Deposit Modal
  const DepositModal = () => (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => {
        // Close modal when clicking on the overlay
        if (e.target === e.currentTarget) {
          setShowDepositModal(false);
        }
      }}
    >
      <div className="modal-content" style={{ maxWidth: '28rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Deposit USDC</h2>
          <button 
            onClick={() => setShowDepositModal(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            &times;
          </button>
        </div>
        
        {actionError && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#fef2f2',
            color: '#b91c1c',
            borderRadius: '0.5rem',
            fontSize: '0.9rem'
          }}>
            {actionError}
          </div>
        )}
        
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            color: '#374151',
            fontWeight: 500
          }}>
            Amount (USDC)
          </label>
          <input
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="0"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          marginTop: '1rem'
        }}>
          <button
            onClick={() => setShowDepositModal(false)}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            disabled={actionLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleWithdraw}
            style={{
              padding: '0.5rem 1.5rem',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            disabled={actionLoading || !withdrawAmount}
          >
            {actionLoading ? 'Processing...' : 'Withdraw'}
          </button>
        </div>
      </div>
    </div>
  );

  // Withdraw Modal
  const WithdrawModal = () => (
    <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && setShowWithdrawModal(false)}>
      <div className="modal-content" style={{ maxWidth: '28rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Withdraw USDC</h2>
          <button 
            onClick={() => setShowWithdrawModal(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            &times;
          </button>
        </div>
        
        {actionError && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#fef2f2',
            color: '#b91c1c',
            borderRadius: '0.5rem',
            fontSize: '0.9rem'
          }}>
            {actionError}
          </div>
        )}
        
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            color: '#374151',
            fontWeight: 500
          }}>
            Amount (USDC)
          </label>
          <input
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="0"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          marginTop: '1rem'
        }}>
          <button
            onClick={() => setShowWithdrawModal(false)}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            disabled={actionLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleWithdraw}
            style={{
              padding: '0.5rem 1.5rem',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            disabled={actionLoading || !withdrawAmount}
          >
            {actionLoading ? 'Processing...' : 'Withdraw'}
          </button>
        </div>
      </div>
    </div>
  );

  // Error Toast
  const ErrorToast = () => (
    <div 
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        zIndex: 1001,
        maxWidth: '320px',
        animation: 'slideIn 0.3s ease-out',
        pointerEvents: 'auto',
      }}
    >
      <span>⚠️</span>
      <span style={{ flex: 1 }}>{actionError}</span>
      <button 
        onClick={handleCloseErrorToast}
        style={{
          background: 'none',
          border: 'none',
          color: '#b91c1c',
          cursor: 'pointer',
          padding: '0.25rem'
        }}
      >
        &times;
      </button>
    </div>
  );

  // Success Toast
  const SuccessToast = () => (
    <div 
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        backgroundColor: '#ecfdf5',
        color: '#065f46',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        zIndex: 1001,
        maxWidth: '320px',
        animation: 'slideIn 0.3s ease-out',
        pointerEvents: 'auto',
      }}
    >
      <span>✅</span>
      <span style={{ flex: 1 }}>{successMessage}</span>
      <button 
        onClick={() => setShowSuccessToast(false)}
        style={{
          background: 'none',
          border: 'none',
          color: '#065f46',
          cursor: 'pointer',
          padding: '0.25rem'
        }}
      >
        &times;
      </button>
    </div>
  );

  const globalStyles = `
    @keyframes slideIn {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    
    /* Modal styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
      backdrop-filter: blur(4px);
    }
    
    .modal-content {
      background: white;
      padding: 1.5rem;
      border-radius: 0.75rem;
      width: 100%;
      max-width: 32rem;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      animation: modalFadeIn 0.3s ease-out;
    }
    
    @keyframes modalFadeIn {
      from { opacity: 0; transform: translateY(-20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    /* Spinner animation */
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .spinner {
      width: 2.5rem;
      height: 2.5rem;
      border: 3px solid rgba(79, 70, 229, 0.2);
      border-radius: 50%;
      border-top-color: #4f46e5;
      animation: spin 1s ease-in-out infinite;
    }
    
    /* Loading overlay */
    .loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1100;
      backdrop-filter: blur(2px);
      pointer-events: auto;
    }
    
    /* Prevent body scroll when modal is open */
    body.modal-open {
      overflow: hidden;
    }
  `;

  return (
    <div className={styles.container} style={{ padding: '0 0.5rem 1rem', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{globalStyles}</style>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1rem', 
        padding: '1rem 0.5rem', 
        borderBottom: '1px solid #e5e9f2', 
        marginBottom: '1rem',
        flexWrap: 'wrap',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              padding: '0.5rem 1rem', 
              background: '#f3f4f6', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '0.95rem', 
              fontWeight: 500 
            }}
          >
            ← Back
          </button>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>{bond.mint?.name || 'Bond Detail'}</h1>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => { 
              setActionError(''); 
              setDepositAmount(''); 
              setShowDepositModal(true); 
            }}
            style={{
              padding: '0.5rem 1rem',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4338ca'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4f46e5'}
          >
            Deposit
          </button>
          <button 
            onClick={() => { 
              setActionError(''); 
              setWithdrawAmount(''); 
              setShowWithdrawModal(true); 
            }}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#4f46e5',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
          >
            Withdraw
          </button>
        </div>
      </div>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          background: 'white', 
          padding: '1rem', 
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>APY</h3>
          <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
            {bond.currentIssuance?.interestRateBps ? (bond.currentIssuance.interestRateBps / 100).toFixed(2) : 'N/A'}%
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>Current Rate</p>
        </div>
        <div style={{ 
          background: 'white', 
          padding: '1rem', 
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>Supply</h3>
          <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
            {bond.mint?.supply ? formatNumber(bond.mint.supply) : 'N/A'}
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>Tokens</p>
        </div>
        <div style={{ 
          background: 'white', 
          padding: '1rem', 
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>Issuer</h3>
          <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 500, wordBreak: 'break-word' }}>
            {(() => {
              const attrs = Array.isArray(bond.mint?.attributes) ? bond.mint.attributes : [];
              const issuer = attrs.find(a => a.trait_type === 'Underlying Asset Issuer');
              return issuer ? issuer.value : 'N/A';
            })()}
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>Bond Issuer</p>
        </div>
        <div style={{ 
          background: 'white', 
          padding: '1rem', 
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>Price</h3>
          <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
            {bond.currentIssuance?.startingTokenAmount ? `$${formatNumber(bond.currentIssuance.startingTokenAmount, 4)}` : 'N/A'}
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>Initial Price</p>
        </div>
      </div>
      
      {/* Current Issuance Section Moved Here */}
      <div style={{ 
        margin: '0 0 1.5rem', 
        background: 'white', 
        borderRadius: '0.75rem', 
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 1.25rem' }}>Current Issuance</h2>
        {(() => {
          const issuance = bond.currentIssuance || {};
          const formatDate = (timestamp) => {
            if (!timestamp) return 'N/A';
            try {
              const date = new Date(timestamp);
              return date.toDateString();
            } catch (e) {
              logger.error('Error formatting date:', e);
              return 'N/A';
            }
          };
          
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Start Date</div>
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>{formatDate(issuance.startDate)}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>End Date</div>
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>{formatDate(issuance.endDate)}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Token Amount</div>
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>
                  {issuance.startingTokenAmount ? `$${formatNumber(issuance.startingTokenAmount, 4)}` : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Status</div>
                <div>
                  <span style={{
                    background: issuance.status === 1 ? '#10b981' : '#ef4444',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    display: 'inline-block'
                  }}>
                    {issuance.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      {/* Bond Summary Section */}
      <div style={{ 
        marginBottom: '1.5rem', 
        background: 'white', 
        borderRadius: '0.75rem', 
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 1.25rem' }}>Bond Summary</h2>
        {(() => {
          const attrs = Array.isArray(bond.mint?.attributes) ? bond.mint.attributes : [];
          const getAttr = (type) => {
            const found = attrs.find(a => a.trait_type === type);
            return found ? found.value : 'N/A';
          };
          
          const mintAddress = bond.mint?.address || bond.address;
          const formatAddress = (address) => {
            if (!address) return 'N/A';
            const str = typeof address === 'string' ? address : (address.toBase58?.() || '');
            return `${str.slice(0, 4)}...${str.slice(-4)}`;
          };
          
          const copyToClipboard = (text) => {
            navigator.clipboard.writeText(text);
            // Optional: Add a toast notification here
          };
          
          const openInSolscan = (address) => {
            if (!address) return;
            const baseUrl = 'https://solscan.io/account/';
            window.open(`${baseUrl}${address}`, '_blank');
          };
          
          return (
            <>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
                <b>Symbol:</b> {bond.mint?.symbol || bond.symbol || 'N/A'}
              </div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <b>Mint Address:</b> 
                <span style={{ fontFamily: 'monospace' }}>{mintAddress ? formatAddress(mintAddress) : 'N/A'}</span>
                {mintAddress && (
                  <>
                    <button 
                      onClick={() => copyToClipboard(mintAddress)}
                      style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer' }}
                      title="Copy to clipboard"
                    >
                      ⎘
                    </button>
                    <button 
                      onClick={() => openInSolscan(mintAddress)}
                      style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer' }}
                      title="View on Solscan"
                    >
                      ↗
                    </button>
                  </>
                )}
              </div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
                <b>Bond Type:</b> {getAttr('Bond Type')}
              </div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
                <b>Description:</b> {bond.mint?.longDescription || bond.description || 'N/A'}
              </div>
            </>
          );
        })()}
        {bond.attributes && (
          <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
            <b>Attributes:</b>
            <ul>
              {Object.keys(bond.attributes).map((key, index) => (
                <li key={index}>{key}: {bond.attributes[key]}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {/* Key Attributes Section */}
      <div style={{ 
        background: 'white', 
        borderRadius: '0.75rem', 
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 1.25rem' }}>Key Attributes</h2>
        {(() => {
          const attrs = Array.isArray(bond.mint?.attributes) ? bond.mint.attributes : [];
          const getAttr = (type) => {
            const found = attrs.find(a => a.trait_type === type);
            return found ? found.value : 'N/A';
          };
          return (
            <>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}><b>Underlying Asset Issuer:</b> {getAttr('Underlying Asset Issuer')}</div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}><b>Underlying Asset Regulatory Body:</b> {getAttr('Underlying Asset Regulatory Body')}</div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}><b>Underlying Asset Credit Rating:</b> {getAttr('Underlying Asset Credit Rating')}</div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
                <b>Registration:</b> <ClickableLink url={getAttr('Registration')} />
              </div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
                <b>Registration MD5:</b> <ClickableLink url={getAttr('Registration MD5')} />
              </div>
              <div style={{ color: '#334155', fontSize: '1rem', marginBottom: '0.7rem' }}>
                <b>Terms of Service:</b> <ClickableLink url={getAttr('Terms of Service')} />
              </div>
            </>
          );
        })()}

      </div>
      
      {/* Modals */}
      {showDepositModal && <DepositModal />}
      {showWithdrawModal && <WithdrawModal />}
      {showErrorToast && <ErrorToast />}
      {showSuccessToast && <SuccessToast />}
      {actionLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            backdropFilter: 'blur(2px)',
            pointerEvents: 'auto'
          }}
          aria-busy="true"
          aria-live="polite"
        >
          <div className={styles.spinner} />
        </div>
      )}
    </div>
  );
};

export default BondDetail;
